import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  agentAdmin,
  brain,
  fetchBrainHealth,
  fetchDaemonState,
  AgentTask,
  AgentDecision,
  AgentMemory,
  HomeostasisSummary,
} from '../lib/api'
import usePolling from '../hooks/usePolling'

// --- Types ---

interface DaemonState {
  daemon_id: string
  daemon_version: string
  current_phase: number
  loop_count: number
  active_task_id: string | null
  last_heartbeat: string
}

interface BrainHealth {
  status: string
  timestamp: string
  active_daemons: number
  pending_tasks: number
}

interface DashboardData {
  health: BrainHealth | null
  daemons: DaemonState[]
  tasks: AgentTask[]
  decisions: AgentDecision[]
  memory: AgentMemory | null
  homeostasis: HomeostasisSummary | null
}

// --- Pulse Item ---

function PulseItem({
  label,
  value,
  color,
  onClick,
}: {
  label: string
  value: string | number
  color: string
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
        onClick ? 'hover:bg-gray-800 cursor-pointer' : 'cursor-default'
      } ${color}`}
    >
      <span className="text-gray-400">{label}</span>
      <span className="font-bold">{value}</span>
    </button>
  )
}

// --- Task Status Badge ---

function TaskStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-900/40 text-yellow-300',
    in_progress: 'bg-blue-900/40 text-blue-300',
    completed: 'bg-green-900/40 text-green-300',
    failed: 'bg-red-900/40 text-red-300',
    cancelled: 'bg-gray-800 text-gray-400',
  }
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors[status] || colors.pending}`}
    >
      {status.replace('_', ' ')}
    </span>
  )
}

// --- Help Tip (inline expandable help) ---

function HelpTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex ml-1">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        className="w-4 h-4 rounded-full bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-gray-200 text-[9px] font-bold inline-flex items-center justify-center transition"
        title="What is this?"
      >
        ?
      </button>
      {open && (
        <div className="absolute z-50 left-6 top-0 w-64 p-2.5 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-[11px] text-gray-300 leading-relaxed whitespace-normal">
          {text}
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false) }}
            className="block mt-1.5 text-[10px] text-gray-500 hover:text-gray-300"
          >
            Got it
          </button>
        </div>
      )}
    </span>
  )
}

// --- Panel Container ---

function Panel({
  title,
  badge,
  children,
  defaultOpen = true,
}: {
  title: string
  badge?: number
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/50 transition"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-3 h-3 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
          {badge !== undefined && badge > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand-600 text-white">
              {badge}
            </span>
          )}
        </div>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  )
}

// --- Main Component ---

export default function AgentDashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Active panel tab for task filtering
  const [taskFilter, setTaskFilter] = useState<string>('all')
  const [creatingTask, setCreatingTask] = useState(false)
  const [newTaskPrompt, setNewTaskPrompt] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<string>('user_initiated')
  const [newTaskType, setNewTaskType] = useState<string>('llm_reasoning')

  // Redirect non-admins
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard')
    }
  }, [user, navigate])

  // Polling data
  const fetcher = useCallback(async (): Promise<DashboardData> => {
    const [healthData, daemonData, tasksData, decisionsData, memoryData, homeoData] =
      await Promise.allSettled([
        fetchBrainHealth(),
        fetchDaemonState(),
        agentAdmin.getTasks(),
        agentAdmin.getDecisions(),
        agentAdmin.getMemory(),
        agentAdmin.getDashboardSummary(),
      ])

    return {
      health:
        healthData.status === 'fulfilled' ? healthData.value : null,
      daemons:
        daemonData.status === 'fulfilled' ? daemonData.value : [],
      tasks:
        tasksData.status === 'fulfilled'
          ? tasksData.value.tasks || []
          : [],
      decisions:
        decisionsData.status === 'fulfilled'
          ? decisionsData.value.decisions || decisionsData.value || []
          : [],
      memory:
        memoryData.status === 'fulfilled' ? memoryData.value : null,
      homeostasis:
        homeoData.status === 'fulfilled' ? homeoData.value : null,
    }
  }, [])

  const { data, loading, error, isRefreshing } = usePolling({
    fetcher,
    interval: 8000,
  })

  const health = data?.health
  const daemons = data?.daemons || []
  const tasks = data?.tasks || []
  const decisions = data?.decisions || []
  const memory = data?.memory
  const homeo = data?.homeostasis

  // Derived stats
  const pendingTasks = tasks.filter((t) => t.status === 'pending').length
  const runningTasks = tasks.filter((t) => t.status === 'in_progress').length
  const failedTasks = tasks.filter((t) => t.status === 'failed').length
  const completedToday = tasks.filter((t) => {
    if (t.status !== 'completed' || !t.completed_at) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return new Date(t.completed_at) >= today
  }).length
  const pendingDecisions = Array.isArray(decisions)
    ? decisions.filter((d) => d.status === 'pending').length
    : 0
  const activeDaemons = daemons.filter((d) => {
    const age = Date.now() - new Date(d.last_heartbeat).getTime()
    return age < 5 * 60 * 1000
  })

  // Filtered tasks
  const filteredTasks =
    taskFilter === 'all'
      ? tasks
      : tasks.filter((t) => t.status === taskFilter)

  // Submit a new task to the Brain
  const handleCreateTask = async () => {
    if (!newTaskPrompt.trim()) return
    setCreatingTask(true)
    try {
      await brain.submitTask({
        type: newTaskType,
        priority: newTaskPriority,
        payload: {
          prompt: newTaskPrompt.trim(),
          model: 'sonnet',
          max_turns: 5,
        },
      })
      setNewTaskPrompt('')
    } catch (e) {
      console.error('Failed to create task:', e)
    } finally {
      setCreatingTask(false)
    }
  }

  // Cancel a task
  const handleCancelTask = async (taskId: string) => {
    try {
      await agentAdmin.cancelTask(taskId)
    } catch (e) {
      console.error('Failed to cancel task:', e)
    }
  }

  // Resolve a decision
  const handleResolveDecision = async (decisionId: string, optionIndex: number) => {
    try {
      await agentAdmin.resolveDecision(decisionId, optionIndex)
    } catch (e) {
      console.error('Failed to resolve decision:', e)
    }
  }

  if (user?.role !== 'admin') return null

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading agent dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-100">Agent Dashboard</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Paradigm management & system monitoring
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div
                className={`w-2 h-2 rounded-full animate-pulse ${
                  isRefreshing
                    ? 'bg-yellow-500'
                    : error
                      ? 'bg-red-500'
                      : 'bg-green-500'
                }`}
              />
              <span>
                {isRefreshing ? 'Refreshing...' : error ? 'Error' : 'Live'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* System Pulse Bar */}
      <div className="px-4 py-2 border-b border-gray-800 bg-gray-950">
        <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto">
          <PulseItem
            label="Daemons"
            value={activeDaemons.length}
            color={activeDaemons.length > 0 ? 'text-green-400' : 'text-red-400'}
          />
          <div className="w-px h-4 bg-gray-800" />
          <PulseItem
            label="Pending"
            value={pendingTasks}
            color={pendingTasks > 0 ? 'text-yellow-400' : 'text-gray-400'}
          />
          <PulseItem
            label="Running"
            value={runningTasks}
            color={runningTasks > 0 ? 'text-blue-400' : 'text-gray-400'}
          />
          <PulseItem
            label="Failed"
            value={failedTasks}
            color={failedTasks > 0 ? 'text-red-400' : 'text-gray-400'}
          />
          <PulseItem
            label="Done Today"
            value={completedToday}
            color="text-green-400"
          />
          <div className="w-px h-4 bg-gray-800" />
          <PulseItem
            label="Decisions"
            value={pendingDecisions}
            color={pendingDecisions > 0 ? 'text-orange-400' : 'text-gray-400'}
          />
          {health && (
            <>
              <div className="w-px h-4 bg-gray-800" />
              <PulseItem
                label="Brain"
                value={health.status === 'ok' ? 'Healthy' : health.status}
                color={
                  health.status === 'ok' ? 'text-green-400' : 'text-red-400'
                }
              />
            </>
          )}
          {homeo?.walker && (
            <>
              <div className="w-px h-4 bg-gray-800" />
              <PulseItem
                label="Walker"
                value={`${homeo.walker.phase === 'canon' ? 'Canon' : 'Mined'} #${homeo.walker.cycle ?? '?'}`}
                color="text-purple-400"
              />
              <PulseItem
                label="Claims"
                value={homeo.walker.claims_processed?.toLocaleString() ?? '0'}
                color="text-cyan-400"
              />
            </>
          )}
          {homeo?.quality_gate && (
            <>
              <PulseItem
                label="Noise"
                value={`${((homeo.quality_gate.noise_ratio ?? 0) * 100).toFixed(0)}%`}
                color={
                  (homeo.quality_gate.noise_ratio ?? 0) > 0.95
                    ? 'text-red-400'
                    : (homeo.quality_gate.noise_ratio ?? 0) > 0.85
                      ? 'text-yellow-400'
                      : 'text-green-400'
                }
              />
              <PulseItem
                label="Rejected"
                value={homeo.quality_gate.rejected?.toLocaleString() ?? '0'}
                color="text-red-400"
              />
            </>
          )}
          {homeo?.budget?.current_state && (
            <>
              <div className="w-px h-4 bg-gray-800" />
              <PulseItem
                label="Budget"
                value={homeo.budget.current_state}
                color={
                  homeo.budget.current_state === 'NORMAL'
                    ? 'text-green-400'
                    : homeo.budget.current_state === 'WARNING'
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }
              />
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Error */}
        {error && !data && (
          <div className="bg-red-950 border border-red-800 rounded-lg p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* System Summary — plain-English interpretation */}
        {homeo && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-200">System Summary</h3>
              <HelpTip text="This is a plain-English summary of what the autonomous system is doing right now. It reads conversation logs, extracts knowledge, quality-checks it, and builds a structured knowledge graph — all without your intervention." />
            </div>
            <div className="text-[12px] text-gray-400 leading-relaxed space-y-1.5">
              {(() => {
                const w = homeo.walker
                const m = homeo.miner
                const q = homeo.quality_gate
                const todos = homeo.pending_todos ?? 0
                const lines: string[] = []
                if (w) {
                  const phase = w.phase === 'canon'
                    ? `reviewing canon claims (Idea ${(w.idea_index ?? 0) + 1} of ${w.idea_count ?? '?'})`
                    : 'reviewing mined claims from conversations'
                  lines.push(`The walker is on cycle ${w.cycle ?? '?'}, currently ${phase}. It has processed ${w.claims_processed?.toLocaleString() ?? 0} claims total and made ${w.graph_mutations?.toLocaleString() ?? 0} knowledge graph updates.`)
                }
                if (q && q.total_evaluated) {
                  const pct = ((q.noise_ratio ?? 0) * 100).toFixed(0)
                  const health = (q.noise_ratio ?? 0) < 0.5 ? 'healthy' : (q.noise_ratio ?? 0) < 0.85 ? 'moderate' : 'high'
                  lines.push(`The quality gate has reviewed ${q.total_evaluated.toLocaleString()} claims — ${q.approved?.toLocaleString() ?? 0} approved, ${q.rejected?.toLocaleString() ?? 0} rejected (${pct}% noise, which is ${health}).`)
                }
                if (m && m.messages_processed) {
                  const yieldPct = m.claims_found && m.messages_processed > 0
                    ? ((m.claims_found / m.messages_processed) * 100).toFixed(1)
                    : '0'
                  lines.push(`The miner has scanned ${m.messages_processed.toLocaleString()} conversation messages and extracted ${m.claims_found?.toLocaleString() ?? 0} potential claims (${yieldPct}% yield).`)
                }
                if (todos > 0) {
                  lines.push(`There are ${todos} pending TODOs waiting for your review — mostly promotion candidates the system thinks should be added to canon.`)
                }
                return lines.map((line, i) => <p key={i}>{line}</p>)
              })()}
            </div>
          </div>
        )}

        {/* Task Submission */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-200">Submit Task to Agent</h3>
            <HelpTip text="Send a task to the agent daemon. The daemon picks it up, executes it using an LLM or subprocess, and reports the result back. Tasks appear in the Tasks panel below." />
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTaskPrompt}
              onChange={(e) => setNewTaskPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateTask()
              }}
              placeholder="Describe a task for the agent..."
              className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <div className="relative group">
              <select
                value={newTaskType}
                onChange={(e) => setNewTaskType(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="llm_reasoning">Think (LLM)</option>
                <option value="metabolism_cycle">Metabolism</option>
                <option value="code_execution">Run Code</option>
                <option value="internal_state_update">Update State</option>
              </select>
              <div className="absolute z-50 hidden group-hover:block right-0 top-full mt-1 w-56 p-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-[10px] text-gray-400 leading-relaxed">
                <p className="mb-1"><span className="text-gray-200 font-semibold">Think (LLM):</span> Agent reasons about a question using Claude</p>
                <p className="mb-1"><span className="text-gray-200 font-semibold">Metabolism:</span> Run the claim extraction pipeline</p>
                <p className="mb-1"><span className="text-gray-200 font-semibold">Run Code:</span> Execute a script or command</p>
                <p><span className="text-gray-200 font-semibold">Update State:</span> Modify internal system state</p>
              </div>
            </div>
            <div className="relative group">
              <select
                value={newTaskPriority}
                onChange={(e) => setNewTaskPriority(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="blocking">Blocking</option>
                <option value="user_initiated">Normal</option>
                <option value="scheduled">Scheduled</option>
                <option value="self_improvement">Background</option>
              </select>
              <div className="absolute z-50 hidden group-hover:block right-0 top-full mt-1 w-56 p-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-[10px] text-gray-400 leading-relaxed">
                <p className="mb-1"><span className="text-gray-200 font-semibold">Blocking:</span> Urgent — runs before everything else</p>
                <p className="mb-1"><span className="text-gray-200 font-semibold">Normal:</span> Standard priority, you asked for this</p>
                <p className="mb-1"><span className="text-gray-200 font-semibold">Scheduled:</span> Runs at next available slot</p>
                <p><span className="text-gray-200 font-semibold">Background:</span> Low priority, agent works on it when idle</p>
              </div>
            </div>
            <button
              onClick={handleCreateTask}
              disabled={!newTaskPrompt.trim() || creatingTask}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
            >
              {creatingTask ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>

        {/* Homeostasis Panels */}
        {homeo && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Knowledge Pipeline */}
            <Panel title="Knowledge Pipeline">
              <div className="space-y-4">
                {/* Walker */}
                {homeo.walker && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Walker</h4>
                      <HelpTip text="The walker traverses the knowledge graph in cycles. In 'Canon' phase, it reviews existing canonical claims. In 'Mined' phase, it reviews new claims extracted from your conversations. Claims = total reviewed, Mutations = graph updates (new links, scores), Promoted = approved into canon." />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-gray-800/50 rounded">
                        <span className="text-[10px] text-gray-500 block">Phase</span>
                        <span className={`text-sm font-bold ${
                          homeo.walker.phase === 'canon' ? 'text-blue-400' : 'text-purple-400'
                        }`}>
                          {homeo.walker.phase === 'canon' ? 'Canon' : 'Mined'}
                        </span>
                      </div>
                      <div className="p-2 bg-gray-800/50 rounded">
                        <span className="text-[10px] text-gray-500 block">Cycle</span>
                        <span className="text-sm font-bold text-gray-200">
                          {homeo.walker.cycle ?? '—'}
                        </span>
                      </div>
                    </div>
                    {/* Progress bar */}
                    {homeo.walker.phase === 'canon' && homeo.walker.idea_count && homeo.walker.idea_count > 0 && (
                      <div className="mt-2">
                        <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                          <span>Idea {(homeo.walker.idea_index ?? 0) + 1} of {homeo.walker.idea_count}</span>
                          <span>{Math.round(((homeo.walker.idea_index ?? 0) / homeo.walker.idea_count) * 100)}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${((homeo.walker.idea_index ?? 0) / homeo.walker.idea_count) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-cyan-400 block">
                          {homeo.walker.claims_processed?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Claims</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-emerald-400 block">
                          {homeo.walker.graph_mutations?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Mutations</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-violet-400 block">
                          {homeo.walker.mined_promoted?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Promoted</span>
                      </div>
                    </div>
                    {homeo.walker.last_processed_at && (
                      <p className="text-[10px] text-gray-600 mt-1.5">
                        Last: {new Date(homeo.walker.last_processed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Miner */}
                {homeo.miner && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Conversation Miner</h4>
                      <HelpTip text="Reads through your conversation logs and extracts factual claims — decisions you made, architecture choices, design rationale. Messages = analyzed messages, Claims Found = extracted statements, Skipped = system messages / too short to analyze. Yield = what % of messages contain extractable knowledge." />
                    </div>
                    {homeo.miner.total_files && homeo.miner.total_files > 0 && (
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                          <span>File {homeo.miner.file_index ?? 0} of {homeo.miner.total_files}</span>
                          <span>{Math.round(((homeo.miner.file_index ?? 0) / homeo.miner.total_files) * 100)}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${((homeo.miner.file_index ?? 0) / homeo.miner.total_files) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-blue-400 block">
                          {homeo.miner.messages_processed?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Messages</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-emerald-400 block">
                          {homeo.miner.claims_found?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Claims Found</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-gray-400 block">
                          {homeo.miner.messages_skipped?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Skipped</span>
                      </div>
                    </div>
                    {homeo.miner.claims_found && homeo.miner.messages_processed && homeo.miner.messages_processed > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1.5">
                        Yield: {((homeo.miner.claims_found / homeo.miner.messages_processed) * 100).toFixed(1)}%
                        {' | '}Cycle {homeo.miner.cycle ?? '?'}
                      </p>
                    )}
                  </div>
                )}

                {/* Quality Gate */}
                {homeo.quality_gate && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Quality Gate</h4>
                      <HelpTip text="An LLM reviews each mined claim before it enters the knowledge graph. Rejected = noise, duplicates, or vague statements. Approved = meaningful claims promoted into canon. Noise Ratio = % rejected. Lower is better — under 50% means the miner is finding mostly good stuff." />
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-red-400 block">
                          {homeo.quality_gate.rejected?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Rejected</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-green-400 block">
                          {homeo.quality_gate.approved?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Approved</span>
                      </div>
                      <div className="text-center p-1.5 bg-gray-800/30 rounded">
                        <span className="text-xs font-bold text-gray-300 block">
                          {homeo.quality_gate.total_evaluated?.toLocaleString() ?? '0'}
                        </span>
                        <span className="text-[9px] text-gray-500">Total</span>
                      </div>
                    </div>
                    {/* Noise ratio bar */}
                    {homeo.quality_gate.noise_ratio != null && (
                      <div>
                        <div className="flex justify-between text-[10px] mb-1">
                          <span className="text-gray-500">Noise Ratio</span>
                          <span className={
                            homeo.quality_gate.noise_ratio > 0.95 ? 'text-red-400' :
                            homeo.quality_gate.noise_ratio > 0.85 ? 'text-yellow-400' :
                            'text-green-400'
                          }>
                            {(homeo.quality_gate.noise_ratio * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden flex">
                          <div
                            className="h-full bg-red-500/70"
                            style={{ width: `${homeo.quality_gate.noise_ratio * 100}%` }}
                          />
                          <div
                            className="h-full bg-green-500/70"
                            style={{ width: `${(1 - homeo.quality_gate.noise_ratio) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Panel>

            {/* TODO Health & Supervisor */}
            <Panel title="System Health">
              <div className="space-y-4">
                {/* Pending TODOs */}
                <div>
                  <div className="flex items-center mb-2">
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Pending TODOs</h4>
                    <HelpTip text="Action items the system created for you. 'canon_walker_promotion' = claims ready for your approval to add to canon. 'canon_walker' = issues found during graph walking. 'idea_formation' = suggestions for new Ideas. Review these on the TODOs page." />
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-2xl font-bold text-amber-400">
                      {homeo.pending_todos ?? 0}
                    </span>
                    <span className="text-[10px] text-gray-500">pending</span>
                  </div>
                  {homeo.todo_breakdown && Object.keys(homeo.todo_breakdown).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(homeo.todo_breakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([source, count]) => (
                          <div key={source} className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-400 truncate mr-2">{source}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-amber-500/60 rounded-full"
                                  style={{ width: `${(count / (homeo.pending_todos || 1)) * 100}%` }}
                                />
                              </div>
                              <span className="text-gray-300 font-medium w-6 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Supervisor / Daemons */}
                {homeo.supervisor && Object.keys(homeo.supervisor).length > 0 && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Supervisor</h4>
                      <HelpTip text="Background processes running on your machine. 'agent' = the main daemon that executes tasks and runs the knowledge pipeline. 'metabolism' = the subprocess that handles claim extraction. Green dot = running, red = stopped. High restart counts may indicate instability." />
                    </div>
                    <div className="space-y-1.5">
                      {Object.entries(homeo.supervisor).map(([name, info]) => {
                        const upHours = info.uptime_seconds
                          ? Math.floor(info.uptime_seconds / 3600)
                          : 0
                        const upMins = info.uptime_seconds
                          ? Math.floor((info.uptime_seconds % 3600) / 60)
                          : 0
                        return (
                          <div key={name} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${info.pid ? 'bg-green-400' : 'bg-red-400'}`} />
                              <span className="text-xs text-gray-200 font-medium">{name}</span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-gray-500">
                              {info.pid && <span>PID {info.pid}</span>}
                              {info.uptime_seconds != null && (
                                <span>{upHours}h {upMins}m</span>
                              )}
                              {info.restarts != null && info.restarts > 0 && (
                                <span className="text-yellow-400">{info.restarts} restarts</span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Budget */}
                {homeo.budget && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold text-gray-400 uppercase">Budget</h4>
                      <HelpTip text="LLM API spending guard. NORMAL = full capability. WARNING = cheaper models preferred. HARD_LOCK = local models only, no cloud API calls. Tracks weekly token usage against a configurable limit." />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                      <span className={`text-sm font-bold ${
                        homeo.budget.current_state === 'NORMAL' ? 'text-green-400' :
                        homeo.budget.current_state === 'WARNING' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {homeo.budget.current_state}
                      </span>
                      {homeo.budget.weekly_limit != null && homeo.budget.weekly_tokens_used != null && (
                        <span className="text-[10px] text-gray-500">
                          {homeo.budget.weekly_tokens_used.toLocaleString()} / {homeo.budget.weekly_limit.toLocaleString()} tokens
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {homeo.warnings && homeo.warnings.length > 0 && (
                  <div>
                    <h4 className="text-[11px] font-semibold text-yellow-400 uppercase mb-2">
                      Warnings
                    </h4>
                    <div className="space-y-1">
                      {homeo.warnings.map((w, i) => (
                        <p key={i} className="text-[11px] text-yellow-400/80 p-1.5 bg-yellow-900/20 rounded">
                          {w}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          </div>
        )}

        {/* Idea Claim Distribution */}
        {homeo?.walker?.idea_claim_counts && Object.keys(homeo.walker.idea_claim_counts).length > 0 && (
          <Panel title="Claim Distribution by Idea" defaultOpen={false}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(homeo.walker.idea_claim_counts)
                .sort(([, a], [, b]) => b - a)
                .map(([idea, count]) => {
                  const maxCount = Math.max(...Object.values(homeo.walker!.idea_claim_counts!))
                  return (
                    <div key={idea} className="p-2 bg-gray-800/50 rounded">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-[11px] text-gray-300 font-medium truncate mr-1">{idea}</span>
                        <span className="text-[10px] text-cyan-400 font-bold">{count.toLocaleString()}</span>
                      </div>
                      <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500/50 rounded-full"
                          style={{ width: `${(count / maxCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
            </div>
          </Panel>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Tasks Panel */}
          <Panel title="Tasks" badge={pendingTasks + runningTasks}>
            {/* Task filter tabs */}
            <div className="flex gap-1 mb-3">
              {['all', 'pending', 'in_progress', 'completed', 'failed'].map(
                (f) => (
                  <button
                    key={f}
                    onClick={() => setTaskFilter(f)}
                    className={`px-2 py-1 text-[11px] font-medium rounded transition ${
                      taskFilter === f
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    }`}
                  >
                    {f === 'in_progress' ? 'Running' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                )
              )}
            </div>

            {/* Task list */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {filteredTasks.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">
                  No {taskFilter === 'all' ? '' : taskFilter} tasks
                </p>
              ) : (
                filteredTasks.slice(0, 20).map((task) => (
                  <div
                    key={task.id}
                    className="p-3 bg-gray-800/50 rounded-lg border border-gray-800 hover:border-gray-700 transition"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs text-gray-200 font-medium line-clamp-2 flex-1">
                        {task.description ||
                          (task.params?.prompt as string) ||
                          task.task_type ||
                          task.id.slice(0, 8)}
                      </p>
                      <TaskStatusBadge status={task.status} />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500">
                      <span>{task.task_type} | {task.priority}</span>
                      <div className="flex gap-1">
                        {task.status === 'pending' && (
                          <button
                            onClick={() => handleCancelTask(task.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Cancel
                          </button>
                        )}
                        {task.created_at && (
                          <span>
                            {new Date(task.created_at).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {task.error && (
                      <p className="text-[10px] text-red-400 mt-1 line-clamp-2">
                        {task.error}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </Panel>

          {/* Decisions Panel */}
          <Panel title="Pending Decisions" badge={pendingDecisions}>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {!Array.isArray(decisions) || decisions.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">
                  No pending decisions
                </p>
              ) : (
                decisions
                  .filter((d) => d.status === 'pending')
                  .map((decision) => (
                    <div
                      key={decision.id}
                      className="p-3 bg-gray-800/50 rounded-lg border border-orange-900/30"
                    >
                      <p className="text-xs text-gray-200 font-medium mb-1">
                        {decision.question}
                      </p>
                      {decision.options && decision.options.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {decision.options.map(
                            (opt: string, idx: number) => (
                              <button
                                key={idx}
                                onClick={() =>
                                  handleResolveDecision(decision.id, idx)
                                }
                                className="px-2 py-1 text-[10px] font-medium bg-orange-900/30 text-orange-300 rounded hover:bg-orange-800/40 transition"
                              >
                                {opt}
                              </button>
                            )
                          )}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-500 mt-1">
                        {decision.created_at &&
                          new Date(decision.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </Panel>

          {/* Daemon Status */}
          <Panel title="Daemon Status" badge={activeDaemons.length}>
            <div className="space-y-2">
              {daemons.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">
                  No daemon registrations
                </p>
              ) : (
                daemons
                  .sort(
                    (a, b) =>
                      new Date(b.last_heartbeat).getTime() -
                      new Date(a.last_heartbeat).getTime()
                  )
                  .slice(0, 5)
                  .map((daemon) => {
                    const age =
                      Date.now() -
                      new Date(daemon.last_heartbeat).getTime()
                    const isActive = age < 5 * 60 * 1000
                    const isStale = age > 60 * 1000 && age < 5 * 60 * 1000

                    return (
                      <div
                        key={daemon.daemon_id}
                        className="p-3 bg-gray-800/50 rounded-lg border border-gray-800"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                daemon.active_task_id
                                  ? 'bg-blue-400 animate-pulse'
                                  : isActive
                                    ? 'bg-green-400'
                                    : isStale
                                      ? 'bg-yellow-400'
                                      : 'bg-gray-600'
                              }`}
                            />
                            <span className="text-xs font-medium text-gray-200">
                              {daemon.daemon_id}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-500">
                            v{daemon.daemon_version}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-500">
                          <span>Phase {daemon.current_phase}</span>
                          <span>Loops: {daemon.loop_count}</span>
                          {daemon.active_task_id && (
                            <span className="text-blue-400">
                              Task: {daemon.active_task_id.slice(0, 8)}
                            </span>
                          )}
                          <span>
                            {age < 60000
                              ? `${Math.floor(age / 1000)}s ago`
                              : age < 3600000
                                ? `${Math.floor(age / 60000)}m ago`
                                : `${Math.floor(age / 3600000)}h ago`}
                          </span>
                        </div>
                      </div>
                    )
                  })
              )}
            </div>
          </Panel>

          {/* Memory Panel */}
          <Panel title="Agent Memory" defaultOpen={false}>
            {memory ? (
              <div className="space-y-3">
                {memory.goals && (
                  <div>
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase mb-1">
                      Goals
                    </h4>
                    <pre className="text-xs text-gray-300 bg-gray-800/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                      {typeof memory.goals === 'string'
                        ? memory.goals
                        : JSON.stringify(memory.goals, null, 2)}
                    </pre>
                  </div>
                )}
                {memory.current_activity && (
                  <div>
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase mb-1">
                      Current Activity
                    </h4>
                    <pre className="text-xs text-gray-300 bg-gray-800/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(memory.current_activity, null, 2)}
                    </pre>
                  </div>
                )}
                {memory.orchestration_state && (
                  <div>
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase mb-1">
                      Orchestration
                    </h4>
                    <pre className="text-xs text-gray-300 bg-gray-800/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(memory.orchestration_state, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500 py-4 text-center">
                No agent memory available
              </p>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
